import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin, Star } from "lucide-react";
import { BRAND } from "@/lib/data";
import { reopenConsent } from "@/components/ConsentBanner";

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#143D63] text-[#F9F8F6] relative z-10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-24 pb-10">
        <div className="grid md:grid-cols-12 gap-12 pb-16 border-b border-[#F9F8F6]/15">
          <div className="md:col-span-5">
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] font-light">
              Let&apos;s design the<br />home you deserve.
            </h2>
            <Link
              to="/consultation"
              data-testid="footer-book-btn"
              className="inline-block mt-8 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#F9F8F6] transition-colors duration-300"
            >
              Book a Free Consultation
            </Link>
          </div>

          <div className="md:col-span-3 md:col-start-8">
            <p className="overline text-[#F8C21C] mb-5">Explore</p>
            <ul className="space-y-3 text-[#F9F8F6]/80">
              <li><Link to="/" className="link-underline">Home</Link></li>
              <li><Link to="/gallery" className="link-underline">Gallery</Link></li>
              <li><Link to="/commercial" className="link-underline">Commercial</Link></li>
              <li><Link to="/ai-studio" className="link-underline">AI Studio</Link></li>
              <li><Link to="/estimate" className="link-underline">Budget Estimator</Link></li>
              <li><Link to="/pricing" className="link-underline">Pricing &amp; Warranty</Link></li>
              <li><Link to="/guides" className="link-underline">Free Guides</Link></li>
              <li><Link to="/about" className="link-underline">About</Link></li>
              <li><Link to="/studio" className="link-underline">My Studio (customer login)</Link></li>
              <li><Link to="/consultation" className="link-underline">Consultation</Link></li>
              <li><Link to="/privacy" className="link-underline">Privacy Policy</Link></li>
              <li><Link to="/terms" className="link-underline">Terms of Service</Link></li>
              <li><Link to="/admin" className="link-underline">Admin</Link></li>
              <li>
                <button data-testid="footer-cookie-prefs" onClick={reopenConsent} className="link-underline text-left">
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#F8C21C] mb-5">Contact</p>
            <ul className="space-y-3 text-[#F9F8F6]/80 text-sm">
              <li><a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-2 link-underline"><Phone size={15} /> {BRAND.phone}</a></li>
              <li><a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 link-underline"><Mail size={15} /> {BRAND.email}</a></li>
              <li className="flex items-start gap-2"><MapPin size={15} className="mt-1 shrink-0" /> {BRAND.address}</li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a href={BRAND.instagram} aria-label="Instagram" className="hover:text-[#F8C21C] transition-colors"><Instagram size={20} /></a>
              <a href={BRAND.facebook} aria-label="Facebook" className="hover:text-[#F8C21C] transition-colors"><Facebook size={20} /></a>
            </div>
            <a
              href={BRAND.reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-review-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-4 py-2.5 text-xs font-bold hover:bg-[#F9F8F6] transition-colors duration-300"
            >
              <Star size={14} fill="#143D63" /> Review us on Google
            </a>
          </div>
        </div>

        <div className="mt-16" data-testid="footer-map">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <p className="overline text-[#F8C21C]">Visit our studio</p>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("ZEBO India Private Limited, " + BRAND.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-directions"
              className="text-sm text-[#F9F8F6]/80 link-underline"
            >
              Get directions on Google Maps &rarr;
            </a>
          </div>
          <div className="border border-[#F9F8F6]/15 overflow-hidden aspect-[21/9] w-full">
            <iframe
              title="ZEBO India office location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(BRAND.address)}&output=embed`}
              className="w-full h-full grayscale-[0.2]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            />
          </div>
        </div>

        <div className="pt-8 mt-10 border-t border-[#F9F8F6]/15 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="bg-[#F9F8F6] px-4 py-2 inline-flex">
            <img src={BRAND.logo} alt={BRAND.full} className="h-14 w-auto object-contain" />
          </div>
          <div className="text-center sm:text-right">
            <p className="text-xs text-[#F9F8F6]/50">© {new Date().getFullYear()} {BRAND.full}. All rights reserved.</p>
            <p className="text-[11px] text-[#F9F8F6]/40 mt-1" data-testid="footer-legal-identifiers">CIN: {BRAND.cin} · GSTIN: {BRAND.gstin}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
